import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { SplatMesh, SparkControls, SparkRenderer, dyno } from '@ydrive/sparky';
import cr3ateBird from '../assets/logo-small.gif';
import cr3ateLogo from '../assets/cr3ate-ai-logo.svg';
import type { XRayPipeData } from '../data/kimbarkBathroomPipes';
import type { ObbTakeoffData } from '../data/obbTakeoff';
import type { WallTakeoffData } from '../data/kimbarkBasementWallTakeoff';

import ViewerOrchestrationAgent from './ViewerOrchestrationAgent';
import EditSceneTool, { type PlaceableAsset } from './EditSceneTool';
import SelectionTool from './SelectionTool';
import MeasureTool from './MeasureTool';
import PeekBehindWallTool, { PeekBorder } from './PeekBehindWallTool';
import FloorMapPanel from './FloorMapPanel';
import { KIMBARK_PLACES } from '../data/kimbarkPlaces';
import type { KimbarkFloor } from '../data/kimbarkRooms';
import { ObbTakeoffOverlay } from './ObbTakeoffOverlay';
import { ObbTakeoffPanel } from './ObbTakeoffPanel';
import { ProgressComparisonOverlay, COMPARISON_AREAS, type ComparisonArea } from './ProgressComparisonOverlay';
import { WallTakeoffOverlay } from './WallTakeoffOverlay';
import { WallTakeoffPanel } from './WallTakeoffPanel';
import type { TrellisGeneratedAsset } from '../services/trellis.service';
import {
  buildPeekSelectionVolume,
  getOrientedSelectionWorldCorners,
  projectScreenSelectionToPlane,
  type WorldPos,
} from '../utils/measureUtils';
import SceneLabelsLayer, { type SceneLabelItem } from './sceneInteractions/SceneLabelsLayer';
import { resolveAssetUrl } from '../utils/assetUrl';
import { SceneCapabilityStates, SceneConfig } from '../types/sceneConfig';
import type { ViewerAgentChatResponse } from '../types/viewerAgent';
import { SparkRaycaster } from '../utils/SparkViewer';
import type { ActiveContextSelection, ViewerSpatialContext } from '../utils/viewerSpatialUtils';

export interface MultiSceneStage {
  url: string;
  label: string;
}

interface MultiSceneViewerProps {
  stages: MultiSceneStage[];
  sceneId: string;
  sceneName?: string;
  siSessionNames?: string[];
  siContextNote?: string;
  title?: string;
  onBack?: () => void;
  defaultCameraPos?: [number, number, number];
  defaultLookAt?: [number, number, number];
  stageDisplayNames?: string[];
  xrayPipeData?: XRayPipeData;
  takeoffItems?: ObbTakeoffData[];
  wallTakeoffData?: WallTakeoffData;
}

const PROGRESSION_CAPABILITIES: SceneCapabilityStates = Object.freeze({
  measurement: { state: 'NOT_AVAILABLE' as const, reason: 'Measurement is not available in progression view.' },
  navigation: { state: 'NOT_AVAILABLE' as const, reason: 'Navigation is not available in progression view.' },
  imageView: { state: 'NOT_AVAILABLE' as const, reason: 'Image view is not available in progression view.' },
  pois: { state: 'NOT_AVAILABLE' as const, reason: 'No assets are available in progression view.' },
  flythrough: { state: 'NOT_AVAILABLE' as const, reason: 'Flythrough is not available in progression view.' },
  hasGeoAlignment: false,
  hasImagePoses: false,
  hasPathNetwork: false,
  supportsAerialFlythrough: false,
  supportsOrbitFlythrough: false,
  supportsPoseWalkthrough: false,
  supportsRecording: false,
  supportsCoordinateConversion: false,
  hasTimeLapse: true,
});

type BlendMode = 0 | 1 | 2;

const MODE = { FADE: 0, WIPE: 1, DISSOLVE: 2 } as const;

// X-Ray splat space quaternion — matches the sceneQuaternion applied to SplatMeshes so
// pipe vertices (raw Z-up) map into Three.js Y-up world space correctly.
const SPLAT_QUATERNION = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
const YAXIS = new THREE.Vector3(0, 1, 0);

function computeObbWorldParams(obbRaw: [number, number, number][]) {
  const rawCx = obbRaw.reduce((s, p) => s + p[0], 0) / 8;
  const rawCy = obbRaw.reduce((s, p) => s + p[1], 0) / 8;
  const rawCz = obbRaw.reduce((s, p) => s + p[2], 0) / 8;
  const center = new THREE.Vector3(rawCx, rawCz, -rawCy);
  const p0 = new THREE.Vector3(...obbRaw[0]);
  const p1 = new THREE.Vector3(...obbRaw[1]);
  const p2 = new THREE.Vector3(...obbRaw[2]);
  const p4 = new THREE.Vector3(...obbRaw[4]);
  const e1 = new THREE.Vector3().subVectors(p1, p0);
  const e2 = new THREE.Vector3().subVectors(p2, p0);
  const e3 = new THREE.Vector3().subVectors(p4, p0);
  const halfLen1 = e1.length() / 2;
  const halfLen2 = e2.length() / 2;
  const halfLen3 = e3.length() / 2;
  const axisX = e1.normalize().applyQuaternion(SPLAT_QUATERNION);
  const dir2 = e2.normalize().applyQuaternion(SPLAT_QUATERNION);
  const dir3 = e3.normalize().applyQuaternion(SPLAT_QUATERNION);
  // Use the more vertical axis (larger |Y| in world space) as axisY for the zigzag pattern
  const [axisY, halfY, axisZ, halfZ] = Math.abs(dir3.y) >= Math.abs(dir2.y)
    ? [dir3, halfLen3, dir2, halfLen2]
    : [dir2, halfLen2, dir3, halfLen3];
  return {
    center,
    halfSize: new THREE.Vector3(halfLen1, halfY, halfZ),
    axisX,
    axisY,
    axisZ,
  };
}

function buildPipeGroup(segments: XRayPipeData['segments'], rawToWorldMatrix?: readonly number[]): THREE.Group {
  const group = new THREE.Group();

  // If a raw→world matrix is provided, vertices will be pre-transformed to world
  // space before placing tube geometry, so we do NOT apply any group quaternion.
  // Without the matrix, fall back to the original convention: vertices are in raw
  // Z-up space and SPLAT_QUATERNION rotates them to Three.js Y-up world space.
  let xformMatrix: THREE.Matrix4 | null = null;
  if (rawToWorldMatrix && rawToWorldMatrix.length === 16) {
    // THREE.Matrix4.set() takes arguments in row-major order.
    xformMatrix = new THREE.Matrix4().set(
      rawToWorldMatrix[0],  rawToWorldMatrix[1],  rawToWorldMatrix[2],  rawToWorldMatrix[3],
      rawToWorldMatrix[4],  rawToWorldMatrix[5],  rawToWorldMatrix[6],  rawToWorldMatrix[7],
      rawToWorldMatrix[8],  rawToWorldMatrix[9],  rawToWorldMatrix[10], rawToWorldMatrix[11],
      rawToWorldMatrix[12], rawToWorldMatrix[13], rawToWorldMatrix[14], rawToWorldMatrix[15],
    );
  } else {
    group.quaternion.copy(SPLAT_QUATERNION);
  }

  const toWorld = (x: number, y: number, z: number): THREE.Vector3 => {
    const v = new THREE.Vector3(x, y, z);
    if (xformMatrix) v.applyMatrix4(xformMatrix);
    return v;
  };

  const coreMat = new THREE.MeshBasicMaterial({
    color: 0x00d4ff,
    transparent: true,
    opacity: 0.9,
    depthTest: false,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const glowMat = new THREE.MeshBasicMaterial({
    color: 0x0080aa,
    transparent: true,
    opacity: 0.35,
    depthTest: false,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  const CORE_R = 0.022;
  const GLOW_R = 0.055;

  const addTube = (
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
  ) => {
    const p0 = toWorld(x0, y0, z0);
    const p1 = toWorld(x1, y1, z1);
    const dir = p1.clone().sub(p0);
    const length = dir.length();
    if (length < 1e-4) return;
    const unitDir = dir.clone().normalize();
    const center = p0.clone().add(p1).multiplyScalar(0.5);
    const rot = new THREE.Quaternion().setFromUnitVectors(YAXIS, unitDir);

    for (const [mat, r] of [[coreMat, CORE_R], [glowMat, GLOW_R]] as const) {
      const mesh = new THREE.Mesh(new THREE.CylinderGeometry(r, r, length, 8, 1), mat);
      mesh.position.copy(center);
      mesh.quaternion.copy(rot);
      mesh.renderOrder = 10;
      group.add(mesh);
    }
  };

  const addSphere = (x: number, y: number, z: number) => {
    const p = toWorld(x, y, z);
    for (const [mat, r] of [[coreMat, CORE_R * 1.3], [glowMat, GLOW_R * 1.2]] as const) {
      const mesh = new THREE.Mesh(new THREE.SphereGeometry(r, 7, 7), mat);
      mesh.position.copy(p);
      mesh.renderOrder = 10;
      group.add(mesh);
    }
  };

  for (const seg of segments) {
    const v = seg.vertices;
    for (let i = 0; i < v.length - 1; i++) {
      addTube(v[i][0], v[i][1], v[i][2], v[i + 1][0], v[i + 1][1], v[i + 1][2]);
    }
    for (const [x, y, z] of v) addSphere(x, y, z);
  }

  return group;
}

function disposePipeGroup(group: THREE.Group) {
  const disposedMaterials = new Set<THREE.Material>();
  group.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.geometry.dispose();
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    for (const m of mats) {
      if (!disposedMaterials.has(m)) { m.dispose(); disposedMaterials.add(m); }
    }
  });
}

export default function MultiSceneViewer({
  stages,
  sceneId,
  sceneName,
  siSessionNames,
  siContextNote,
  title = 'Construction Progress',
  onBack,
  defaultCameraPos,
  defaultLookAt,
  stageDisplayNames,
  xrayPipeData,
  takeoffItems,
  wallTakeoffData,
}: MultiSceneViewerProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const threeSceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const [progress, setProgress] = useState(0);
  const [mode, setMode] = useState<BlendMode>(MODE.FADE);
  const [axis, setAxis] = useState(0);
  const [softness, setSoftness] = useState(0.08);
  const [playing, setPlaying] = useState(false);
  const [openPanel, setOpenPanel] = useState<'timelapse' | 'floormap' | 'teleport' | null>(null);
  const [floorMapHighlight, setFloorMapHighlight] = useState<{ roomName: string; floor: KimbarkFloor } | null>(null);
  // 'iconstrip' = mini panels on top; 'overlay' = fixed tool panels on top.
  const [frontLayer, setFrontLayer] = useState<'iconstrip' | 'overlay'>('iconstrip');
  const [loaded, setLoaded] = useState(0);
  const [loadingDetail, setLoadingDetail] = useState('Preparing captures…');
  const [stageProgress, setStageProgress] = useState<number[]>([]);
  const stageProgressRef = useRef<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Selection tool
  const [selectionActive, setSelectionActive] = useState(false);
  const [agentSelectionMode, setAgentSelectionMode] = useState<'click' | 'box' | 'lasso'>('box');

  // Measurement tool
  const [measureActive, setMeasureActive] = useState(false);
  const [measureInitialPoints, setMeasureInitialPoints] = useState<WorldPos[] | undefined>(undefined);
  const [measureKey, setMeasureKey] = useState(0);

  // Selection-driven peek tool (independent from the Kimbark hard-coded demo).
  const [selectionPeekOpen, setSelectionPeekOpen] = useState(false);
  const [peekPreConfigured, setPeekPreConfigured] = useState(false);
  const peekPreConfiguredBoundsRef = useRef<{ min: [number,number,number]; max: [number,number,number] } | null>(null);
  const selectionPeekTargetRef = useRef(0);

  // Scene labels (from SI showSceneLabels action)
  const [sceneLabels, setSceneLabels] = useState<SceneLabelItem[]>([]);
  const activeContextSelectionRef = useRef<ActiveContextSelection | null>(null);
  const pendingSelectionQuestionRef = useRef<string | null>(null);
  const lastUserMessageRef = useRef<string | null>(null);
  const externalSubmitRef = useRef<((text: string) => void) | null>(null);
  const meshesRef = useRef<SplatMesh[]>([]);
  const progressRef = useRef(0);
  useEffect(() => { progressRef.current = progress; }, [progress]);


  // X-Ray tube overlay
  const [xrayActive, setXrayActive] = useState(false);
  const xrayGroupRef = useRef<THREE.Group | null>(null);

  // Takeoff overlay — index into takeoffItems, or null when closed
  const [activeTakeoffIndex, setActiveTakeoffIndex] = useState<number | null>(null);

  // Wall takeoff overlay — activated by agent 'showWallTakeoff' action
  const [wallTakeoffActive, setWallTakeoffActive] = useState(false);
  const [wallTakeoffHighlightedId, setWallTakeoffHighlightedId] = useState<string | null>(null);

  // Progress comparison — activated by agent 'showProgressComparison'; pill click selects specific area
  const [progressComparisonReady, setProgressComparisonReady] = useState(false);
  const [activeComparisonArea, setActiveComparisonArea] = useState<ComparisonArea | null>(null);
  const progressComparisonReadyRef = useRef(false);
  useEffect(() => { progressComparisonReadyRef.current = progressComparisonReady; }, [progressComparisonReady]);
  const [comparisonLeftStage, setComparisonLeftStage] = useState(0);
  const [comparisonRightStage, setComparisonRightStage] = useState(0);
  const comparisonObbParamsRef = useRef<{ center: THREE.Vector3; halfSize: THREE.Vector3; axisX: THREE.Vector3; axisY: THREE.Vector3; axisZ: THREE.Vector3; splitMode?: 'horizontal' } | null>(null);
  const comparisonLeftStageRef = useRef(0);
  const comparisonRightStageRef = useRef(0);
  useEffect(() => { comparisonLeftStageRef.current = comparisonLeftStage; }, [comparisonLeftStage]);
  useEffect(() => { comparisonRightStageRef.current = comparisonRightStage; }, [comparisonRightStage]);

  // Peek window (shader-based spatial reveal of scan 1 through the wall)
  const [peekActive, setPeekActive] = useState(false);
  const peekTargetRef = useRef(0);            // 0 = off, 1 = on — written by toggle, read by animation loop
  const peekStrengthDynoRef = useRef<ReturnType<typeof dyno.dynoFloat> | null>(null);
  // World-space corners of the projected opening face — drives the border overlay
  const [peekBorderCorners, setPeekBorderCorners] = useState<THREE.Vector3[] | null>(null);

  // EditSceneTool (model placement + erase) — targets the final stage only.
  // All placed objects and erase masks live on the last SplatMesh / finalStageGroup
  // and are hidden while the user is viewing earlier stages.
  const [editSceneActive, setEditSceneActive] = useState(false);
  const [generatedAssets, setGeneratedAssets] = useState<PlaceableAsset[]>([]);
  const [initialGeneratedAssetId, setInitialGeneratedAssetId] = useState<string | undefined>();
  const [editAssetLabel, setEditAssetLabel] = useState<string | undefined>();
  const [agentEraseBoxes, setAgentEraseBoxes] = useState<Array<{ id: string; min: THREE.Vector3; max: THREE.Vector3 }>>([]);
  const [agentEraseRegion, setAgentEraseRegion] = useState<{ center: [number, number, number]; radius: number } | null>(null);
  const [agentPlacement, setAgentPlacement] = useState<{ catalogueId?: string; label: string; targetBounds?: { min: THREE.Vector3; max: THREE.Vector3 }; dimensions?: { width?: number; depth?: number; length?: number }; glbUrl?: string } | null>(null);
  const [eraseModeToken, setEraseModeToken] = useState(0);
  const eraseBoxCounterRef = useRef(0);
  // Group that holds all placed GLB objects. Added to the Three.js scene once
  // on mount and toggled visible only when the final stage is active.
  const finalStageGroupRef = useRef<THREE.Group | null>(null);

  // Teleport / camera readout
  const cameraReadoutRef = useRef<HTMLPreElement>(null);
  const teleportInputRef = useRef<HTMLInputElement>(null);
  const teleportLookRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const tmp = new THREE.Vector3();
    const id = setInterval(() => {
      const cam = cameraRef.current;
      const el = cameraReadoutRef.current;
      if (!cam || !el) return;
      const p = cam.position;
      cam.getWorldDirection(tmp);
      const lx = p.x + tmp.x * 8;
      const ly = p.y + tmp.y * 8;
      const lz = p.z + tmp.z * 8;
      el.textContent =
        `pos  ${p.x.toFixed(3)}, ${p.y.toFixed(3)}, ${p.z.toFixed(3)}\n` +
        `look ${lx.toFixed(3)}, ${ly.toFixed(3)}, ${lz.toFixed(3)}`;
    }, 120);
    return () => clearInterval(id);
  }, []);

  // Synthetic viewer object that SelectionTool, EditSceneTool, and viewerSpatialUtils expect.
  // `splatMesh` always returns the LAST mesh so erase always targets the final stage.
  // `scene` returns the finalStageGroup so placed GLB objects live there and toggle with it.
  const multiViewerRef = useRef({
    get camera() { return cameraRef.current ?? undefined; },
    get renderer() { return rendererRef.current ?? undefined; },
    get threeScene() { return threeSceneRef.current ?? undefined; },
    get scene() { return finalStageGroupRef.current ?? undefined; },
    get splatMesh() {
      const meshes = meshesRef.current;
      if (!meshes.length) return null;
      return meshes[meshes.length - 1];
    },
    raycaster: new SparkRaycaster(),
    getRenderDimensions(out: THREE.Vector2) { rendererRef.current?.getSize(out); },
  });

  const controlsRef = useRef<{
    setProgress: (value: number) => void;
    setMode: (value: BlendMode) => void;
    setAxis: (value: number) => void;
    setSoftness: (value: number) => void;
    resetView: () => void;
    teleportTo: (pos: [number, number, number], lookAt?: [number, number, number]) => void;
    walkTo: (pos: [number, number, number], lookAt: [number, number, number], duration?: number) => void;
    setSelectionPeek: (selection: ActiveContextSelection, stageIndex: number, bgStage?: number) => void;
    setPreConfiguredPeek: (bounds: { min: [number, number, number]; max: [number, number, number] }, stageIndex: number, bgStage?: number, cameraWorldPos?: [number, number, number]) => void;
    clearSelectionPeek: () => void;
    setLocalCompare: (params: { center: THREE.Vector3; halfSize: THREE.Vector3; axisX: THREE.Vector3; axisY: THREE.Vector3; axisZ: THREE.Vector3; leftStage: number; rightStage: number; splitMode?: 'horizontal' }) => void;
    clearLocalCompare: () => void;
    startTimeLapse: () => void;
  } | null>(null);
  const playingRef = useRef(playing);
  const loopingRef = useRef(false);

  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  // Expose the last stage's URL as `gaus` so the agent capability check
  // (hasGaus && !hasManifest) evaluates to true, unlocking 3D scene editing.
  const agentScene = useMemo<SceneConfig>(() => ({
    sceneId,
    sceneName: sceneName ?? title,
    siSessionNames,
    siContextNote,
    gaus: stages.length > 0 ? stages[stages.length - 1].url : undefined,
    navigableRooms: KIMBARK_PLACES.map(p => ({
      key: p.key,
      aliases: p.aliases,
      floor: p.floor,
      ...(p.roomContext ? { roomContext: p.roomContext } : {}),
    })),
  }), [sceneId, sceneName, title, siSessionNames, siContextNote, stages]);

  const sortCornersWindingOrder = useCallback((pts: [number, number, number][]): [number, number, number][] => {
    if (pts.length !== 4) return pts;
    const byY = [...pts].sort((a, b) => a[1] - b[1]);
    const bottom = byY.slice(0, 2).sort((a, b) => a[2] - b[2]);
    const top = byY.slice(2, 4).sort((a, b) => a[2] - b[2]);
    return [bottom[0], bottom[1], top[1], top[0]];
  }, []);

  const handleGeneratedAsset = useCallback((asset: TrellisGeneratedAsset) => {
    if (asset.suggestedDimensions) {
      // Claude pre-computed the right furniture size — skip catalogue selection, place directly.
      setAgentPlacement({
        glbUrl: asset.glbUrl,
        label: asset.label,
        dimensions: asset.suggestedDimensions,
      });
      setEditSceneActive(true);
      return;
    }
    const placeable: PlaceableAsset = {
      id: asset.assetId,
      label: asset.label,
      url: asset.gaussianPlyUrl ?? asset.glbUrl,
      icon: '✨',
      source: 'trellis',
      format: asset.gaussianPlyUrl ? 'splat' : 'glb',
      fallbackUrl: asset.glbUrl,
    };
    setGeneratedAssets(prev => [...prev.filter(a => a.id !== placeable.id), placeable]);
    setInitialGeneratedAssetId(placeable.id);
    setEditSceneActive(true);
  }, []);

  const handleAgentDecision = useCallback((response: ViewerAgentChatResponse) => {
    const action = response.decision?.action ?? 'none';
    if (action === 'activateSelectionTool') {
      pendingSelectionQuestionRef.current = lastUserMessageRef.current;
      setAgentSelectionMode((response.data?.selectionMode as 'click' | 'box' | 'lasso') ?? 'box');
      setSelectionActive(true);
    } else if (action === 'activateMeasurement') {
      setSelectionActive(false);
      const pts = (response.data as Record<string, unknown> | undefined)?.['measureInitialPoints'];
      const rawPts = Array.isArray(pts) && pts.length > 0 ? (pts as [number, number, number][]) : undefined;
      const sorted = rawPts ? sortCornersWindingOrder(rawPts) : undefined;
      setMeasureInitialPoints(sorted?.map(([x, y, z]) => ({ x, y, z })));
      setMeasureKey((k) => k + 1);
      setMeasureActive(true);
    } else if (action === 'showSceneLabels') {
      const labels = (response.data?.labels ?? []) as Array<{ id?: string; text: string; world: [number, number, number]; gps?: { lat: number; lon: number } }>;
      setSceneLabels(labels.map((label, index) => ({
        id: label.id ?? `agent-label-${index}`,
        text: label.text,
        worldPos: { x: label.world[0], y: label.world[1], z: label.world[2] },
        gps: label.gps,
      })));
    } else if (action === 'showWallTakeoff') {
      setWallTakeoffActive(true);
    } else if (action === 'startTimeLapse') {
      controlsRef.current?.startTimeLapse();
    } else if (action === 'showProgressComparison') {
      setProgressComparisonReady(true);
      setActiveComparisonArea(null);
      controlsRef.current?.clearLocalCompare();
      controlsRef.current?.setMode(0 as BlendMode);
      setMode(0 as BlendMode);
      const lastStageIdx = stages.length - 1;
      controlsRef.current?.setProgress(lastStageIdx);
      setProgress(lastStageIdx);
      playingRef.current = false;
      setPlaying(false);
    } else if (action === 'resetView') {
      controlsRef.current?.resetView();
    } else if (action === 'teleportToCoordinates') {
      const target = (response.data as Record<string, unknown> | undefined)?.['teleportTarget'] as
        | { world?: [number, number, number]; lookAtWorld?: [number, number, number] }
        | undefined;
      if (target?.world) {
        controlsRef.current?.teleportTo(target.world, target.lookAtWorld);
      }
    } else if (action === 'navigateToRoom') {
      const placeName = response.data?.placeName;
      const place = placeName ? KIMBARK_PLACES.find(p => p.key === placeName) : undefined;
      if (place) {
        // Stop any active time-lapse so stage labels don't flash during navigation.
        playingRef.current = false;
        setPlaying(false);
        // Upper/basement: snap to second-to-last stage (that floor's latest scan).
        // Ground floor: snap to last stage (ground floor's latest scan is the final stage).
        if (place.floor === 'upper' || place.floor === 'basement') {
          const target = Math.max(0, stages.length - 2);
          controlsRef.current?.setProgress(target);
          setProgress(target);
        } else {
          const target = stages.length - 1;
          controlsRef.current?.setProgress(target);
          setProgress(target);
        }
        controlsRef.current?.walkTo(place.cameraPos, place.cameraLookAt);
        setFloorMapHighlight({ roomName: place.roomName, floor: place.floor });
        setOpenPanel('floormap');
      }
    } else if (action === 'activatePeek') {
      const tt = response.data?.teleportTarget;
      const peekBounds = response.data?.peekBounds;
      const peekStageIndex = response.data?.peekStageIndex ?? 0;
      const peekScreenPoints = response.data?.peekScreenPoints as Array<{ x: number; y: number }> | undefined;
      if (tt?.world) {
        // teleportTo is synchronous — camera.position and quaternion are at the
        // destination immediately after this call returns.
        controlsRef.current?.teleportTo(tt.world, tt.lookAtWorld);
      }
      if (peekBounds && controlsRef.current) {
        const peekBgStageOffset = (response.data?.peekBgStageOffset as number | undefined) ?? 1;
        const bgStage = stages.length - peekBgStageOffset;
        const [minX, minY, minZ] = peekBounds.min;
        const [maxX, maxY, maxZ] = peekBounds.max;
        const cx = (minX + maxX) / 2;
        const cy = (minY + maxY) / 2;
        const cz = (minZ + maxZ) / 2;
        // Read the camera quaternion after teleport so buildPeekSelectionVolume
        // gets camera-aligned axes (axisZ = (0,0,-1).applyQuaternion(cameraQ)
        // points into the wall correctly). Identity quaternion would aim the
        // prism in the wrong direction for a camera facing +Z.
        const cam = multiViewerRef.current.camera;
        const q = cam?.quaternion ?? { x: 0, y: 0, z: 0, w: 1 };
        const syntheticSelection: ActiveContextSelection = {
          mode: 'box',
          worldCenter: { x: cx, y: cy, z: cz },
          worldBounds: {
            min: { x: minX, y: minY, z: minZ },
            max: { x: maxX, y: maxY, z: maxZ },
          },
          orientedBounds: {
            center: { x: cx, y: cy, z: cz },
            size: { x: maxX - minX, y: maxY - minY, z: maxZ - minZ },
            quaternion: [q.x, q.y, q.z, q.w] as [number, number, number, number],
          },
          // Screen points let projectScreenSelectionToPlane produce the same tight
          // window as the original manual draw instead of falling back to the full AABB.
          screenShape: peekScreenPoints ? { kind: 'box', points: peekScreenPoints } : undefined,
          samplePoints: [],
          hitCount: 0,
          attemptedSamples: 0,
          confidence: 'high',
        };
        controlsRef.current.setSelectionPeek(syntheticSelection, peekStageIndex, bgStage);
        setProgress(bgStage);
        setPeekPreConfigured(true);
        // Don't open the peek panel — activate measurement instead so the user
        // can immediately start measuring the revealed area.
        setMeasureActive(true);
      }
    } else if (action === 'activateEditTool') {
      setEditAssetLabel(response.data?.editAssetLabel);
      setEditSceneActive(true);
    } else if (action === 'activateEraseMode') {
      setAgentEraseRegion(null);
      setEraseModeToken(t => t + 1);
      setEditSceneActive(true);
    } else if (action === 'eraseByCoordinates') {
      const aabb = response.data?.eraseAabb;
      const rd = response.data?.eraseRegion;
      if (aabb) {
        const newBox = {
          id: String(++eraseBoxCounterRef.current),
          min: new THREE.Vector3(...aabb.min),
          max: new THREE.Vector3(...aabb.max),
        };
        setAgentEraseBoxes(prev => [...prev, newBox]);
        if (rd) setAgentEraseRegion(rd);
        setEraseModeToken(t => t + 1);
      } else if (rd) {
        setAgentEraseRegion(rd);
      } else {
        setEraseModeToken(t => t + 1);
      }
      setEditSceneActive(true);
    } else if (action === 'placeAndEraseModel') {
      const pm = response.data?.placeModel;
      if (pm) {
        if (pm.eraseFirst) {
          const TOLERANCE = 0.05;
          setAgentEraseBoxes(prev => [...prev, {
            id: String(++eraseBoxCounterRef.current),
            min: new THREE.Vector3(pm.targetBounds.min[0], pm.targetBounds.min[1] - TOLERANCE, pm.targetBounds.min[2]),
            max: new THREE.Vector3(pm.targetBounds.max[0], pm.targetBounds.max[1] + TOLERANCE, pm.targetBounds.max[2]),
          }]);
        }
        setAgentPlacement({
          catalogueId: pm.catalogueId,
          label: pm.label,
          targetBounds: {
            min: new THREE.Vector3(...pm.targetBounds.min),
            max: new THREE.Vector3(...pm.targetBounds.max),
          },
          dimensions: pm.dimensions,
          glbUrl: pm.glbUrl,
        });
      }
      setAgentEraseRegion(null);
      setEditSceneActive(true);
    } else if (action === 'placeGeneratedAsset') {
      const asset = response.data?.generatedAsset;
      if (asset) handleGeneratedAsset(asset as TrellisGeneratedAsset);
    }
  }, [sortCornersWindingOrder, handleGeneratedAsset, stages]);

  const handleUserMessage = useCallback((text: string) => {
    lastUserMessageRef.current = text;
  }, []);

  const handlePillClick = useCallback((tag: string) => {
    // Wall takeoff surface/opening highlighting
    if (wallTakeoffData) {
      // Individual surface tags map directly to surface IDs
      if (tag === 'wall_left' || tag === 'wall_right' || tag === 'wall_middle') {
        setWallTakeoffHighlightedId(prev => prev === tag ? null : tag);
        return;
      }
      // Group tags highlight the first surface of that status
      if (tag === 'complete' || tag === 'remaining' || tag === 'opening' || tag === 'elec') {
        let id: string | null = null;
        if (tag === 'complete')  id = wallTakeoffData.surfaces.find(s => s.status === 'complete')?.id ?? null;
        if (tag === 'remaining') id = wallTakeoffData.surfaces.find(s => s.status === 'remaining')?.id ?? null;
        if (tag === 'opening')   id = '__openings__';
        if (tag === 'elec')      id = '__electrical__';
        setWallTakeoffHighlightedId(prev => prev === id ? null : id);
        return;
      }
    }

    // Comparison area selection — pill click re-opens even if comparison was closed
    const area = COMPARISON_AREAS[tag as ComparisonArea];
    if (!area) return;
    setProgressComparisonReady(true);
    setActiveComparisonArea(tag as ComparisonArea);
    controlsRef.current?.teleportTo(area.data.cameraPos, area.data.cameraLookAt);
    const params = computeObbWorldParams(area.data.obbPoints);
    if (area.flipX) params.axisX.negate();
    comparisonObbParamsRef.current = { ...params, splitMode: area.splitMode };
    const leftStage = stages.length - 2;
    const rightStage = stages.length - 1;
    setComparisonLeftStage(leftStage);
    setComparisonRightStage(rightStage);
    controlsRef.current?.setLocalCompare({ ...params, leftStage, rightStage, splitMode: area.splitMode });
    const takeoffIdx = takeoffItems?.findIndex(item => item === area.data) ?? -1;
    setActiveTakeoffIndex(takeoffIdx >= 0 ? takeoffIdx : null);
  }, [stages.length, wallTakeoffData, takeoffItems]);

  const handleComparisonLeft = useCallback((delta: number) => {
    setComparisonLeftStage(prev => {
      const next = Math.max(0, Math.min(stages.length - 1, prev + delta));
      if (comparisonObbParamsRef.current) {
        controlsRef.current?.setLocalCompare({ ...comparisonObbParamsRef.current, leftStage: next, rightStage: comparisonRightStageRef.current });
      }
      return next;
    });
  }, [stages.length]);

  const handleComparisonRight = useCallback((delta: number) => {
    setComparisonRightStage(prev => {
      const next = Math.max(0, Math.min(stages.length - 1, prev + delta));
      if (comparisonObbParamsRef.current) {
        controlsRef.current?.setLocalCompare({ ...comparisonObbParamsRef.current, leftStage: comparisonLeftStageRef.current, rightStage: next });
      }
      return next;
    });
  }, [stages.length]);

  const getViewerSpatialContext = useCallback((): ViewerSpatialContext | null => {
    const camera = cameraRef.current;
    const cameraWorld = camera
      ? { x: camera.position.x, y: camera.position.y, z: camera.position.z }
      : undefined;
    let lookAtWorld: { x: number; y: number; z: number } | undefined;
    if (camera) {
      const dir = new THREE.Vector3();
      camera.getWorldDirection(dir);
      lookAtWorld = {
        x: camera.position.x + dir.x * 8,
        y: camera.position.y + dir.y * 8,
        z: camera.position.z + dir.z * 8,
      };
    }
    const stageIndex = Math.round(Math.max(0, Math.min(stages.length - 1, progressRef.current)));
    const stageLabel = stages[stageIndex]?.label;
    return {
      ...(cameraWorld ? { cameraWorld } : {}),
      ...(lookAtWorld ? { lookAtWorld, lookAtSource: 'camera-forward' as const } : {}),
      activeTools: { measurementOpen: false, flythroughOpen: false, selectionOpen: selectionActive, extractionOpen: false },
      ...(activeContextSelectionRef.current ? { activeContextSelection: activeContextSelectionRef.current } : {}),
      currentStageIndex: stageIndex,
      ...(stageLabel ? { currentStageLabel: stageLabel } : {}),
    };
  }, [selectionActive, stages]);

  const captureScreenshot = useCallback(async (): Promise<string | null> => {
    const r = rendererRef.current;
    const s = threeSceneRef.current;
    const c = cameraRef.current;
    if (!r || !s || !c) return null;
    r.render(s, c);
    return r.domElement.toDataURL('image/jpeg', 0.92);
  }, []);

  const parseXYZ = useCallback((s: string): [number, number, number] | null => {
    const parts = s.split(',').map(p => parseFloat(p.trim()));
    return parts.length === 3 && parts.every(n => !isNaN(n)) ? (parts as [number, number, number]) : null;
  }, []);

  const handleTeleportKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const pos = parseXYZ(teleportInputRef.current?.value ?? '');
    if (!pos) return;
    const lookAt = parseXYZ(teleportLookRef.current?.value ?? '') ?? undefined;
    controlsRef.current?.teleportTo(pos, lookAt);
  }, [parseXYZ]);

  const togglePeek = useCallback(() => {
    setPeekActive(prev => {
      const next = !prev;
      peekTargetRef.current = next ? 1 : 0;
      return next;
    });
  }, []);

  const toggleTakeoff = useCallback((index: number) => {
    setActiveTakeoffIndex(prev => {
      const next = prev === index ? null : index;
      if (next !== null && takeoffItems?.[next]) {
        const item = takeoffItems[next];
        controlsRef.current?.teleportTo(item.cameraPos, item.cameraLookAt);
      }
      return next;
    });
    setOpenPanel(null);
    setFrontLayer('overlay');
  }, [takeoffItems]);


  const toggleXray = useCallback(() => {
    const scene = threeSceneRef.current;
    if (!scene || !xrayPipeData) return;
    setXrayActive((prev) => {
      if (!prev) {
        const group = buildPipeGroup(xrayPipeData.segments, xrayPipeData.rawToWorldMatrix);
        xrayGroupRef.current = group;
        scene.add(group);
      } else {
        if (xrayGroupRef.current) {
          scene.remove(xrayGroupRef.current);
          disposePipeGroup(xrayGroupRef.current);
          xrayGroupRef.current = null;
        }
      }
      return !prev;
    });
  }, [xrayPipeData]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || stages.length < 1) return;

    setLoaded(0);
    setLoadingDetail('Preparing captures…');
    setError(null);
    setProgress(0);
    let disposed = false;
    let meshes: SplatMesh[] = [];
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, 1, 0.05, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    rendererRef.current = renderer;
    threeSceneRef.current = scene;
    cameraRef.current = camera;
    renderer.setPixelRatio(window.devicePixelRatio);
    // Without explicit CSS sizing, canvas.clientWidth on Retina (dpr>1) equals
    // width*dpr, making PointerControls read a rect twice as large as the
    // visible area — drag-to-look becomes half as sensitive as expected.
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    mount.appendChild(renderer.domElement);

    const spark = new SparkRenderer({ renderer, lodSplatScale: 4 });
    scene.add(spark);
    const controls = new SparkControls({ canvas: renderer.domElement, preset: 'walk' });

    // Group that holds placed GLB objects and their lights. Visibility is toggled
    // in the animation loop so they only appear when the final stage is active.
    const finalStageGroup = new THREE.Group();
    finalStageGroupRef.current = finalStageGroup;
    scene.add(finalStageGroup);

    const progressDyno = dyno.dynoFloat(0);
    const modeDyno = dyno.dynoInt(MODE.FADE);
    const axisDyno = dyno.dynoInt(0);
    const softDyno = dyno.dynoFloat(0.08);
    const rangeMinDyno = dyno.dynoVec3(new THREE.Vector3(-1, -1, -1));
    const rangeMaxDyno = dyno.dynoVec3(new THREE.Vector3(1, 1, 1));

    // Selection-driven peek uniforms. This is a separate tool from the existing
    // hard-coded Kimbark peek, but shares the same per-stage blend pipeline.
    const selectionPeekEnabledDyno = dyno.dynoFloat(0);
    const selectionPeekStageDyno = dyno.dynoInt(0);

    const selectionPeekCenterDyno = dyno.dynoVec3(new THREE.Vector3());
    const selectionPeekHalfSizeDyno = dyno.dynoVec3(new THREE.Vector3(0.05, 0.05, 0.05));
    const selectionPeekAxisXDyno = dyno.dynoVec3(new THREE.Vector3(1, 0, 0));
    const selectionPeekAxisYDyno = dyno.dynoVec3(new THREE.Vector3(0, 1, 0));
    const selectionPeekAxisZDyno = dyno.dynoVec3(new THREE.Vector3(0, 0, 1));
    const selectionPeekCameraDyno = dyno.dynoVec3(new THREE.Vector3());
    const selectionPeekFaceCenterDyno = dyno.dynoVec3(new THREE.Vector3());
    const selectionPeekFaceDepthDyno = dyno.dynoFloat(1);

    // localCompare dynos — OBB-bounded split showing before vs after inside a selected area
    const localCompareEnabledDyno = dyno.dynoFloat(0);
    const localCompareLeftStageDyno = dyno.dynoInt(0);
    const localCompareRightStageDyno = dyno.dynoInt(0);
    const localCompareCenterDyno = dyno.dynoVec3(new THREE.Vector3());
    const localCompareHalfSizeDyno = dyno.dynoVec3(new THREE.Vector3());
    const localCompareAxisXDyno = dyno.dynoVec3(new THREE.Vector3(1, 0, 0));
    const localCompareAxisYDyno = dyno.dynoVec3(new THREE.Vector3(0, 1, 0));
    const localCompareAxisZDyno = dyno.dynoVec3(new THREE.Vector3(0, 0, 1));
    // 0 = zigzag vertical split (kitchen), 1 = horizontal split (staircase/fireplace/cabinet)
    const localCompareSplitModeDyno = dyno.dynoFloat(0);

    // Peek window dynos — bounding box in world space.
    // All scans share the same world coordinate frame (proven by coherent time-lapse).
    // World→raw conversion: raw_x = world_x, raw_y = -world_z, raw_z = world_y
    //
    // Box derived from in-viewer selections on the Kimbark bathroom pipe wall:
    //   Scene 4 tile surface click: world (-1.21, 2.85, 1.07)
    //   Scene 1 pipe region box:    world X[-2.35,-0.32] Y[1.52,2.95] Z[2.09,3.00]
    // One box spans both — tile face (z≈1.07) and the pipe depth behind it (z≈2.09-3.00).
    const peekStrengthDyno = dyno.dynoFloat(0);
    peekStrengthDynoRef.current = peekStrengthDyno;
    const pipeData = xrayPipeData;
    // worldModifier exposes centers in the same world-space frame used by the
    // selection and measured scene bounds.
    const peekMinVec = new THREE.Vector3(...(pipeData?.peekBounds.min ?? [0, 0, 0]));
    const peekMaxVec = new THREE.Vector3(...(pipeData?.peekBounds.max ?? [0, 0, 0]));
    const peekMinDyno = dyno.dynoVec3(peekMinVec);
    const peekMaxDyno = dyno.dynoVec3(peekMaxVec);
    console.info('[peek] init — pipeData:', !!pipeData, 'min:', peekMinVec.toArray(), 'max:', peekMaxVec.toArray());

    // stageIndex: the compile-time index of this stage in the progression.
    // isPeekSource: scan 1 (index 0) has the exposed pipes — reveal inside window.
    // isLatestStage: the finished scan — fade out inside window to expose scan 1.
    function blendModifier(
      stageDyno: ReturnType<typeof dyno.dynoFloat>,
      stageIndex: number,
      isLatestStage: boolean,
    ) {
      const isPeekSource = stageIndex === 0;
      const hasPeek = (isPeekSource || isLatestStage) && !!pipeData;

      return dyno.dynoBlock({ gsplat: dyno.Gsplat }, { gsplat: dyno.Gsplat }, ({ gsplat }) => {
        const modifier = new dyno.Dyno({
          inTypes: {
            gsplat: dyno.Gsplat,
            progress: 'float', stage: 'float', mode: 'int', axis: 'int', soft: 'float',
            rangeMin: 'vec3', rangeMax: 'vec3',
            selectionPeekEnabled: 'float', selectionPeekStage: 'int',
            selectionPeekCenter: 'vec3', selectionPeekHalfSize: 'vec3',
            selectionPeekAxisX: 'vec3', selectionPeekAxisY: 'vec3', selectionPeekAxisZ: 'vec3',
            selectionPeekCamera: 'vec3', selectionPeekFaceCenter: 'vec3', selectionPeekFaceDepth: 'float',
            localCompareEnabled: 'float', localCompareLeftStage: 'int', localCompareRightStage: 'int',
            localCompareCenter: 'vec3', localCompareHalfSize: 'vec3',
            localCompareAxisX: 'vec3', localCompareAxisY: 'vec3', localCompareAxisZ: 'vec3',
            localCompareSplitMode: 'float',
            ...(hasPeek ? { peekStrength: 'float', peekMin: 'vec3', peekMax: 'vec3' } : {}),
          },
          outTypes: { gsplat: dyno.Gsplat },
          globals: () => [dyno.unindent(`
            float splatHash(vec3 p) {
              return fract(sin(dot(p, vec3(12.9898, 78.233, 45.164))) * 43758.5453123);
            }
          `)],
          statements: ({ inputs, outputs }) => dyno.unindentLines(`
            ${outputs.gsplat} = ${inputs.gsplat};
            float soft = max(${inputs.soft}, 1e-4);
            vec3 c = ${inputs.gsplat}.center;
            float d = ${inputs.progress} - ${inputs.stage};
            float weight;
            if (abs(d) >= 1.0) {
              weight = 0.0;
            } else {
              bool incoming = d < 0.0;
              float u = incoming ? (1.0 + d) : d;
              float uWide = u * (1.0 + 2.0 * soft) - soft;
              float wIncoming;
              if (${inputs.mode} == 0) {
                wIncoming = u;
              } else if (${inputs.mode} == 1) {
                int a = ${inputs.axis};
                vec3 rmin = ${inputs.rangeMin};
                vec3 rmax = ${inputs.rangeMax};
                float coord = (a == 0) ? c.x : ((a == 1) ? c.y : c.z);
                float lo = (a == 0) ? rmin.x : ((a == 1) ? rmin.y : rmin.z);
                float hi = (a == 0) ? rmax.x : ((a == 1) ? rmax.y : rmax.z);
                float s = clamp((coord - lo) / max(hi - lo, 1e-6), 0.0, 1.0);
                wIncoming = 1.0 - smoothstep(uWide - soft, uWide + soft, s);
              } else {
                float h = splatHash(c);
                wIncoming = smoothstep(h - soft, h + soft, uWide);
              }
              weight = clamp(incoming ? wIncoming : 1.0 - wIncoming, 0.0, 1.0);
              if (${inputs.mode} == 0) weight = pow(weight, 3.0);
            }
            ${hasPeek ? `
            if (${inputs.peekStrength} > 0.001) {
              vec3 pMin = ${inputs.peekMin};
              vec3 pMax = ${inputs.peekMax};
              if (c.x >= pMin.x && c.x <= pMax.x && c.y >= pMin.y && c.y <= pMax.y && c.z >= pMin.z && c.z <= pMax.z) {
                float ex = min(c.x - pMin.x, pMax.x - c.x) / max(pMax.x - pMin.x, 0.001);
                float ey = min(c.y - pMin.y, pMax.y - c.y) / max(pMax.y - pMin.y, 0.001);
                float ez = min(c.z - pMin.z, pMax.z - c.z) / max(pMax.z - pMin.z, 0.001);
                float wf = clamp(min(min(ex, ey), ez) * 8.0, 0.0, 1.0);
                ${isPeekSource
                  ? `weight = max(weight, ${inputs.peekStrength} * wf);`
                  : `weight = weight * (1.0 - ${inputs.peekStrength} * wf);`
                }
              }
            }` : ''}
            if (${inputs.selectionPeekEnabled} > 0.001) {
              vec3 selectionDelta = c - ${inputs.selectionPeekCenter};
              vec3 selectionLocal = vec3(
                dot(selectionDelta, ${inputs.selectionPeekAxisX}),
                dot(selectionDelta, ${inputs.selectionPeekAxisY}),
                dot(selectionDelta, ${inputs.selectionPeekAxisZ})
              );
              vec3 selectionHalfSize = ${inputs.selectionPeekHalfSize};
              if (abs(selectionLocal.x) <= selectionHalfSize.x &&
                  abs(selectionLocal.y) <= selectionHalfSize.y &&
                  abs(selectionLocal.z) <= selectionHalfSize.z) {
                if (${inputs.selectionPeekStage} == ${stageIndex}) {
                  // The chosen construction scan owns every pixel inside the
                  // opening. Do not mix the finished scan through it.
                  weight = max(weight, ${inputs.selectionPeekEnabled});
                } else {
                  // Suppress every other scan in the opening, not only the
                  // latest one. This keeps intermediate time-lapse stages from
                  // leaking into the peek if progress changes.
                  weight = weight * (1.0 - ${inputs.selectionPeekEnabled});
                }
              }
            }
            if (${inputs.localCompareEnabled} > 0.001) {
              vec3 lc_d = c - ${inputs.localCompareCenter};
              float lc_x = dot(lc_d, ${inputs.localCompareAxisX});
              float lc_y = dot(lc_d, ${inputs.localCompareAxisY});
              float lc_z = dot(lc_d, ${inputs.localCompareAxisZ});
              vec3 lc_h = ${inputs.localCompareHalfSize};
              if (abs(lc_x) <= lc_h.x && abs(lc_y) <= lc_h.y && abs(lc_z) <= lc_h.z) {
                float diag;
                if (${inputs.localCompareSplitMode} < 0.5) {
                  float norm_y2 = (lc_y + lc_h.y) / max(2.0 * lc_h.y, 0.001);
                  float tooth = fract(norm_y2 * 5.0);
                  float zigzag = (tooth < 0.5 ? tooth : 1.0 - tooth) * 2.0 - 0.5;
                  diag = -(lc_x / max(lc_h.x, 0.001)) - zigzag * 0.35;
                } else {
                  float norm_z2 = (lc_z + lc_h.z) / max(2.0 * lc_h.z, 0.001);
                  float tooth_h = fract(norm_z2 * 5.0);
                  float zigzag_h = (tooth_h < 0.5 ? tooth_h : 1.0 - tooth_h) * 2.0 - 0.5;
                  diag = -(lc_y / max(lc_h.y, 0.001)) - zigzag_h * 0.35;
                }
                float t = smoothstep(-0.04, 0.04, diag);
                if (${inputs.localCompareLeftStage} == ${stageIndex}) {
                  weight = 1.0 - t;
                } else if (${inputs.localCompareRightStage} == ${stageIndex}) {
                  weight = t;
                } else {
                  weight = 0.0;
                }
              }
            }
            ${outputs.gsplat}.rgba.w *= weight;
          `),
        });

        gsplat = modifier.apply({
          gsplat,
          progress: progressDyno,
          stage: stageDyno,
          mode: modeDyno,
          axis: axisDyno,
          soft: softDyno,
          rangeMin: rangeMinDyno,
          rangeMax: rangeMaxDyno,
          selectionPeekEnabled: selectionPeekEnabledDyno,
          selectionPeekStage: selectionPeekStageDyno,
          selectionPeekCenter: selectionPeekCenterDyno,
          selectionPeekHalfSize: selectionPeekHalfSizeDyno,
          selectionPeekAxisX: selectionPeekAxisXDyno,
          selectionPeekAxisY: selectionPeekAxisYDyno,
          selectionPeekAxisZ: selectionPeekAxisZDyno,
          selectionPeekCamera: selectionPeekCameraDyno,
          selectionPeekFaceCenter: selectionPeekFaceCenterDyno,
          selectionPeekFaceDepth: selectionPeekFaceDepthDyno,
          localCompareEnabled: localCompareEnabledDyno,
          localCompareLeftStage: localCompareLeftStageDyno,
          localCompareRightStage: localCompareRightStageDyno,
          localCompareCenter: localCompareCenterDyno,
          localCompareHalfSize: localCompareHalfSizeDyno,
          localCompareAxisX: localCompareAxisXDyno,
          localCompareAxisY: localCompareAxisYDyno,
          localCompareAxisZ: localCompareAxisZDyno,
          localCompareSplitMode: localCompareSplitModeDyno,
          ...(hasPeek ? {
            peekStrength: peekStrengthDyno,
            peekMin: peekMinDyno,
            peekMax: peekMaxDyno,
          } : {}),
        }).gsplat;
        return { gsplat };
      });
    }

    let activeMeshes = new Set<SplatMesh>();
    const markDirty = () => {
      const peekIsActive = pipeData != null && (peekTargetRef.current > 0 || peekStrengthDyno.value > 0.001);
      const selectionPeekIsActive = selectionPeekTargetRef.current > 0 || selectionPeekEnabledDyno.value > 0.001;
      const nowActive = new Set(meshes.filter(
        (mesh, index) => {
          const stageDynoVal = (mesh as SplatMesh & { stageDyno: ReturnType<typeof dyno.dynoFloat> }).stageDyno.value;
          const inTimelapse = Math.abs(progressDyno.value - stageDynoVal) < 1;
          // When peek is active, also keep the source (stage 0) and latest stage updated
          // so the peek reveal/fade gets correct sort order on both meshes.
          const isPeekMesh = peekIsActive && (index === 0 || index === meshes.length - 1);
          const isSelectionPeekMesh = selectionPeekIsActive &&
            (index === selectionPeekStageDyno.value || index === meshes.length - 1);
          const localCompareIsActive = localCompareEnabledDyno.value > 0.001;
          const isLocalCompareMesh = localCompareIsActive &&
            (index === localCompareLeftStageDyno.value || index === localCompareRightStageDyno.value);
          return inTimelapse || isPeekMesh || isSelectionPeekMesh || isLocalCompareMesh;
        }
      ));
      for (const mesh of activeMeshes) mesh.updateVersion();
      for (const mesh of nowActive) if (!activeMeshes.has(mesh)) mesh.updateVersion();
      activeMeshes = nowActive;
    };

    const resize = () => {
      const width = mount.clientWidth || window.innerWidth;
      const height = mount.clientHeight || window.innerHeight;
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mount);
    resize();

    let homePosition = new THREE.Vector3(0, 2, 8);
    let homeQuaternion = new THREE.Quaternion();
    let lastTime: number | null = null;
    type WalkAnim = { startPos: THREE.Vector3; endPos: THREE.Vector3; startQuat: THREE.Quaternion; endQuat: THREE.Quaternion; startTime: number; duration: number };
    let walkAnim: WalkAnim | null = null;

    // Vladimir's scene-comparison framing: pool a bounded sample from every
    // capture, reject the outer 1% as stray floaters, and use the combined
    // median as the camera target.
    const measureBounds = (meshList: SplatMesh[]) => {
      const samples: number[][] = [[], [], []];
      const point = new THREE.Vector3();

      for (const mesh of meshList) {
        const stride = Math.max(1, Math.ceil((mesh.numSplats || 0) / 200000));
        let sampleIndex = 0;
        mesh.forEachSplat((_index, center) => {
          if (sampleIndex++ % stride) return;
          point.copy(center).applyMatrix4(mesh.matrixWorld);
          samples[0].push(point.x);
          samples[1].push(point.y);
          samples[2].push(point.z);
        });
      }

      const min = new THREE.Vector3();
      const max = new THREE.Vector3();
      const median = new THREE.Vector3();
      for (let axisIndex = 0; axisIndex < 3; axisIndex++) {
        const values = samples[axisIndex];
        if (!values.length) continue;
        values.sort((left, right) => left - right);
        min.setComponent(axisIndex, values[Math.floor(values.length * 0.01)]);
        max.setComponent(axisIndex, values[Math.floor(values.length * 0.99)]);
        median.setComponent(axisIndex, values[Math.floor(values.length * 0.5)]);
      }
      return { min, max, median };
    };

    const initialize = async () => {
      try {
        console.info(`[MultiSceneViewer] Loading ${stages.length} stages`, stages.map(stage => stage.label));
        const sceneQuaternion = new THREE.Quaternion().setFromEuler(
          new THREE.Euler(-Math.PI / 2, 0, 0)
        );
        stageProgressRef.current = stages.map(() => 0);
        setStageProgress(stageProgressRef.current.slice());
        meshes = stages.map((stage, index) => {
          let lastLoggedPercent = -10;
          console.info(`[MultiSceneViewer] Stage ${stage.label}: starting`, stage.url);
          const mesh = new SplatMesh({
            url: resolveAssetUrl(stage.url) ?? stage.url,
            // Match Vladimir's scene-comparison example. Extended splats use
            // substantially more memory and are not practical when several
            // near-1 GB captures are resident together.
            onProgress: (event: ProgressEvent) => {
              if (disposed) return;
              if (event.lengthComputable && event.total > 0) {
                stageProgressRef.current[index] = event.loaded / event.total;
                setStageProgress(stageProgressRef.current.slice());
                const progressPercent = Math.floor(event.loaded / event.total * 10) * 10;
                if (progressPercent > lastLoggedPercent) {
                  lastLoggedPercent = progressPercent;
                  console.info(
                    `[MultiSceneViewer] Stage ${stage.label}: ${Math.min(progressPercent, 100)}% downloaded`,
                  );
                }
              }
            },
          });
          mesh.quaternion.copy(sceneQuaternion);
          const stageDyno = dyno.dynoFloat(index);
          (mesh as SplatMesh & { stageDyno: typeof stageDyno }).stageDyno = stageDyno;
          mesh.worldModifier = blendModifier(stageDyno, index, index === stages.length - 1);
          scene.add(mesh);
          return mesh;
        });

        await Promise.all(meshes.map((mesh, index) => mesh.initialized.then(() => {
          if (!disposed) {
            setLoaded(value => value + 1);
            setLoadingDetail('Decoding captures…');
            console.info(
              `[MultiSceneViewer] Stage ${stages[index].label}: initialized (${mesh.numSplats.toLocaleString()} splats)`,
            );
          }
        })));
        if (disposed) return;

        meshes.forEach(mesh => mesh.updateMatrixWorld(true));
        meshesRef.current = meshes;
        const bounds = measureBounds(meshes);
        rangeMinDyno.value.copy(bounds.min);
        rangeMaxDyno.value.copy(bounds.max);

        const size = bounds.max.clone().sub(bounds.min);
        const radius = 0.8 * size.length();
        // Start inside the scene at the median, looking toward the -Z face of
        // the bounding box (into the scene rather than from outside).
        // Reset view also returns here — the outside overview framing
        // (0.8 * diagonal) is visible anyway once the user scrolls back.
        if (defaultCameraPos) {
          homePosition = new THREE.Vector3(...defaultCameraPos);
          camera.position.copy(homePosition);
          if (defaultLookAt) camera.lookAt(...defaultLookAt);
          else camera.lookAt(bounds.median.x, bounds.median.y, bounds.min.z);
        } else {
          homePosition = bounds.median.clone();
          camera.position.copy(homePosition);
          camera.lookAt(bounds.median.x, bounds.median.y, bounds.min.z);
        }
        homeQuaternion = camera.quaternion.clone();

        // Default speed is tuned for house-scale scenes. Scale to actual scene
        // size so large aerial captures (100 m+) are reachable by keyboard and
        // scroll without holding WASD for minutes.
        const sceneDiag = size.length();
        controls.fpsMovement.moveSpeed = Math.max(1, sceneDiag / 12);
        controls.pointerControls.scrollSpeed = Math.max(0.003, radius / 1000);
        controls.pointerControls.pressMoveSpeed = controls.fpsMovement.moveSpeed;
        // Expand far plane so large scenes aren't clipped when camera is far out.
        camera.far = Math.max(1000, radius * 5);
        camera.updateProjectionMatrix();

        const lastStage = stages.length - 1;
        modeDyno.value = MODE.FADE;
        progressDyno.value = lastStage;
        markDirty();
        setMode(MODE.FADE);
        setProgress(lastStage);
        console.info('[MultiSceneViewer] All stages ready', {
          stages: stages.length,
          sceneDiag: sceneDiag.toFixed(1),
          moveSpeed: controls.fpsMovement.moveSpeed.toFixed(2),
          scrollSpeed: controls.pointerControls.scrollSpeed.toFixed(4),
          farPlane: camera.far,
          boundsMin: bounds.min.toArray(),
          boundsMax: bounds.max.toArray(),
          target: bounds.median.toArray(),
          cameraPosition: homePosition.toArray(),
        });

        renderer.setAnimationLoop((timeMs) => {
          if (disposed) return;
          const seconds = timeMs * 0.001;
          const dt = Math.min(0.1, seconds - (lastTime ?? seconds));
          lastTime = seconds;
          if (playingRef.current) {
            const lastStage = stages.length - 1;
            const next = Math.min(progressDyno.value + dt / 3, lastStage);
            progressDyno.value = next;
            setProgress(next);
            markDirty();
            if (next >= lastStage) {
              if (loopingRef.current) {
                progressDyno.value = 0;
                setProgress(0);
              } else {
                playingRef.current = false;
                setPlaying(false);
              }
            }
          }
          // Smoothly animate peek window strength toward target (0 or 1)
          const psDyno = peekStrengthDynoRef.current;
          if (psDyno) {
            const diff = peekTargetRef.current - psDyno.value;
            if (Math.abs(diff) > 0.001) {
              const prevVal = psDyno.value;
              psDyno.value += Math.sign(diff) * Math.min(Math.abs(diff), dt * 2.5);
              if (Math.abs(prevVal) < 0.01 || Math.abs(psDyno.value - 1) < 0.01) {
                console.info('[peek] strength animating:', prevVal.toFixed(3), '→', psDyno.value.toFixed(3));
              }
              markDirty();
            }
          }
          const selectionPeekDiff = selectionPeekTargetRef.current - selectionPeekEnabledDyno.value;
          if (Math.abs(selectionPeekDiff) > 0.001) {
            const prevSP = selectionPeekEnabledDyno.value;
            selectionPeekEnabledDyno.value += Math.sign(selectionPeekDiff) *
              Math.min(Math.abs(selectionPeekDiff), dt * 2.5);
            if (prevSP < 0.05 || selectionPeekEnabledDyno.value > 0.99) {
              console.info('[selectionPeek] enabled animating:', prevSP.toFixed(3), '→', selectionPeekEnabledDyno.value.toFixed(3), 'stage:', selectionPeekStageDyno.value, 'progress:', progressDyno.value.toFixed(2));
            }
            markDirty();
          }
          // Placed objects and erase mask only visible on the final stage.
          finalStageGroup.visible = Math.round(progressDyno.value) >= stages.length - 1;
          controls.update(camera, undefined, timeMs);
          if (walkAnim) {
            const elapsed = (timeMs * 0.001) - walkAnim.startTime;
            const raw = Math.min(elapsed / walkAnim.duration, 1);
            const t = raw * raw * (3 - 2 * raw);
            camera.position.lerpVectors(walkAnim.startPos, walkAnim.endPos, t);
            camera.quaternion.slerpQuaternions(walkAnim.startQuat, walkAnim.endQuat, t);
            camera.updateMatrixWorld();
            markDirty();
            if (raw >= 1) walkAnim = null;
          }
          renderer.render(scene, camera);
        });
      } catch (loadError) {
        if (!disposed) {
          console.error('[MultiSceneViewer] Failed to load captures', loadError);
          setError(loadError instanceof Error ? loadError.message : 'Failed to load captures');
        }
      }
    };

    controlsRef.current = {
      setProgress(value) {
        progressDyno.value = Math.min(stages.length - 1, Math.max(0, value));
        markDirty();
      },
      setMode(value) { modeDyno.value = value; markDirty(); },
      setAxis(value) { axisDyno.value = value; markDirty(); },
      setSoftness(value) { softDyno.value = value; markDirty(); },
      resetView() {
        controls.flyTo.cancel();
        controls.pointerControls.rotateVelocity.set(0, 0, 0);
        controls.pointerControls.moveVelocity.set(0, 0, 0);
        controls.pointerControls.scroll.set(0, 0, 0);
        camera.position.copy(homePosition);
        camera.quaternion.copy(homeQuaternion);
        camera.updateMatrixWorld();
        const last = stages.length - 1;
        progressDyno.value = last;
        setProgress(last);
        markDirty();
      },
      teleportTo(pos: [number, number, number], lookAt?: [number, number, number]) {
        walkAnim = null;
        controls.flyTo.cancel();
        controls.pointerControls.rotateVelocity.set(0, 0, 0);
        controls.pointerControls.moveVelocity.set(0, 0, 0);
        controls.pointerControls.scroll.set(0, 0, 0);
        camera.position.set(...pos);
        if (lookAt) camera.lookAt(...lookAt);
        camera.updateMatrixWorld();
      },
      walkTo(pos: [number, number, number], lookAt: [number, number, number], duration = 5.5) {
        controls.flyTo.cancel();
        controls.pointerControls.rotateVelocity.set(0, 0, 0);
        controls.pointerControls.moveVelocity.set(0, 0, 0);
        controls.pointerControls.scroll.set(0, 0, 0);
        const startPos = camera.position.clone();
        const startQuat = camera.quaternion.clone();
        const endPos = new THREE.Vector3(...pos);
        const tempCam = new THREE.PerspectiveCamera();
        tempCam.position.copy(endPos);
        tempCam.lookAt(...lookAt);
        walkAnim = { startPos, endPos, startQuat, endQuat: tempCam.quaternion.clone(), startTime: performance.now() * 0.001, duration };
        markDirty();
      },
      setSelectionPeek(selection, stageIndex, bgStage) {
        if (stageIndex >= stages.length - 1) return;
        if (!selection.orientedBounds) return;

        // Preserve the rectangle the user actually drew. Raycast hits can be
        // sparse and used to shrink the opening to only part of that rectangle.
        const screenPoints = selection.screenShape?.points ?? [];
        const openingBounds = projectScreenSelectionToPlane(
          selection.orientedBounds,
          screenPoints,
          camera,
        ) ?? selection.orientedBounds;
        const opening = buildPeekSelectionVolume(openingBounds);

        const selectedFaceCorners = getOrientedSelectionWorldCorners(selection.orientedBounds);
        const projectedFaceCorners = getOrientedSelectionWorldCorners(openingBounds);
        const reprojectedScreenPoints = projectedFaceCorners.map((point) => {
          const ndc = new THREE.Vector3(point.x, point.y, point.z).project(camera);
          return { x: (ndc.x + 1) * 0.5, y: (1 - ndc.y) * 0.5 };
        });
        console.group('[selection-peek] coordinate comparison');
        console.log('camera world position', camera.position.toArray());
        console.log('drawn screen points (normalized 0..1)', JSON.stringify(screenPoints));
        console.log('projected face returned to screen', JSON.stringify(reprojectedScreenPoints));
        console.table(screenPoints.map((drawn, index) => ({
          corner: index,
          drawnX: drawn.x,
          drawnY: drawn.y,
          projectedX: reprojectedScreenPoints[index]?.x,
          projectedY: reprojectedScreenPoints[index]?.y,
          deltaX: (reprojectedScreenPoints[index]?.x ?? 0) - drawn.x,
          deltaY: (reprojectedScreenPoints[index]?.y ?? 0) - drawn.y,
        })));
        console.log('raycast world bounds', selection.worldBounds);
        console.log('raycast-oriented face', selection.orientedBounds);
        console.table(selectedFaceCorners);
        console.log('drawn rectangle projected onto wall', openingBounds);
        console.table(projectedFaceCorners);
        console.log('opening prism in world space', opening);
        console.log('shader opening prism (world space)', opening);
        console.log('shader stage index', stageIndex, '(UI scene number', stageIndex + 1, ')');
        console.groupEnd();

        selectionPeekCenterDyno.value.set(opening.center.x, opening.center.y, opening.center.z);
        selectionPeekHalfSizeDyno.value.set(opening.halfSize.x, opening.halfSize.y, opening.halfSize.z);
        selectionPeekAxisXDyno.value.set(opening.axisX.x, opening.axisX.y, opening.axisX.z);
        selectionPeekAxisYDyno.value.set(opening.axisY.x, opening.axisY.y, opening.axisY.z);
        selectionPeekAxisZDyno.value.set(opening.axisZ.x, opening.axisZ.y, opening.axisZ.z);
        selectionPeekCameraDyno.value.copy(camera.position);
        selectionPeekFaceCenterDyno.value.set(
          openingBounds.center.x,
          openingBounds.center.y,
          openingBounds.center.z,
        );
        selectionPeekFaceDepthDyno.value = Math.max(
          new THREE.Vector3(
            openingBounds.center.x - camera.position.x,
            openingBounds.center.y - camera.position.y,
            openingBounds.center.z - camera.position.z,
          ).dot(new THREE.Vector3(opening.axisZ.x, opening.axisZ.y, opening.axisZ.z)),
          0.001,
        );
        selectionPeekStageDyno.value = stageIndex;
        selectionPeekTargetRef.current = 1;
        progressDyno.value = bgStage ?? stages.length - 1;
        setPeekBorderCorners(projectedFaceCorners.map((c) => new THREE.Vector3(c.x, c.y, c.z)));
        markDirty();
      },
      clearSelectionPeek() {
        selectionPeekTargetRef.current = 0;
        setPeekBorderCorners(null);
        markDirty();
      },
      setPreConfiguredPeek(bounds, stageIndex, bgStage, cameraWorldPos) {
        if (stageIndex >= stages.length - 1) return;
        const cx = (bounds.min[0] + bounds.max[0]) / 2;
        const cy = (bounds.min[1] + bounds.max[1]) / 2;
        const cz = (bounds.min[2] + bounds.max[2]) / 2;
        const hx = (bounds.max[0] - bounds.min[0]) / 2;
        const hy = (bounds.max[1] - bounds.min[1]) / 2;
        const hz = (bounds.max[2] - bounds.min[2]) / 2;
        // Use caller-supplied camera pos (teleport target) so faceDepth is correct
        // even if the camera animation hasn't resolved yet.
        const camZ = cameraWorldPos?.[2] ?? camera.position.z;
        // faceDepth must equal the Z-distance from camera to prism centre so that
        // selectionPerspective ≈ 1 for splats at the face — matching the manual-draw case.
        const faceDepth = Math.max(Math.abs(cz - camZ), 0.001);
        selectionPeekCenterDyno.value.set(cx, cy, cz);
        selectionPeekHalfSizeDyno.value.set(hx, hy, hz);
        selectionPeekAxisXDyno.value.set(1, 0, 0);
        selectionPeekAxisYDyno.value.set(0, 1, 0);
        selectionPeekAxisZDyno.value.set(0, 0, 1);
        if (cameraWorldPos) {
          selectionPeekCameraDyno.value.set(cameraWorldPos[0], cameraWorldPos[1], cameraWorldPos[2]);
        } else {
          selectionPeekCameraDyno.value.copy(camera.position);
        }
        selectionPeekFaceCenterDyno.value.set(cx, cy, cz);
        selectionPeekFaceDepthDyno.value = faceDepth;
        selectionPeekStageDyno.value = stageIndex;
        selectionPeekTargetRef.current = 1;
        progressDyno.value = bgStage ?? stages.length - 1;
        // Pass world-space AABB face corners — PeekBorder re-projects each frame.
        // Use the target camera Z (cameraWorldPos) to pick front vs back face.
        const camZForFront = cameraWorldPos?.[2] ?? camera.position.z;
        const frontZ = camZForFront < cz ? bounds.min[2] : bounds.max[2];
        setPeekBorderCorners([
          new THREE.Vector3(bounds.min[0], bounds.min[1], frontZ),
          new THREE.Vector3(bounds.max[0], bounds.min[1], frontZ),
          new THREE.Vector3(bounds.max[0], bounds.max[1], frontZ),
          new THREE.Vector3(bounds.min[0], bounds.max[1], frontZ),
        ]);
        markDirty();
      },
      setLocalCompare(params) {
        localCompareCenterDyno.value.copy(params.center);
        localCompareHalfSizeDyno.value.copy(params.halfSize);
        localCompareAxisXDyno.value.copy(params.axisX);
        localCompareAxisYDyno.value.copy(params.axisY);
        localCompareAxisZDyno.value.copy(params.axisZ);
        localCompareLeftStageDyno.value = params.leftStage;
        localCompareRightStageDyno.value = params.rightStage;
        localCompareSplitModeDyno.value = params.splitMode === 'horizontal' ? 1 : 0;
        localCompareEnabledDyno.value = 1.0;
        markDirty();
      },
      clearLocalCompare() {
        localCompareEnabledDyno.value = 0;
        markDirty();
      },
      startTimeLapse() {
        loopingRef.current = false;
        modeDyno.value = MODE.WIPE;
        setMode(MODE.WIPE);
        progressDyno.value = 0;
        setProgress(0);
        playingRef.current = true;
        setPlaying(true);
        markDirty();
      },
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as Element | null;
      if ((event.key === 'h' || event.key === 'H') && !target?.closest('input, textarea, [contenteditable]')) {
        controlsRef.current?.resetView();
      } else if (event.code === 'Space') {
        event.preventDefault();
        const nextPlaying = !playingRef.current;
        if (!nextPlaying) loopingRef.current = false;
        playingRef.current = nextPlaying;
        setPlaying(nextPlaying);
      } else if (event.code === 'Comma' || event.code === 'Period') {
        loopingRef.current = false;
        playingRef.current = false;
        setPlaying(false);
        const direction = event.code === 'Comma' ? -1 : 1;
        const next = Math.min(stages.length - 1, Math.max(0, progressDyno.value + direction * 0.02));
        progressDyno.value = next;
        setProgress(next);
        markDirty();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    // React 18 StrictMode mounts, cleans up, then mounts effects again in
    // development. Starting multi-GB fetches synchronously here lets the probe
    // already-started loader. Deferring one turn lets cleanup cancel the probe
    // before any network request begins.
    const initializeTimer = window.setTimeout(initialize, 0);
    return () => {
      disposed = true;
      selectionPeekTargetRef.current = 0;
      window.clearTimeout(initializeTimer);
      stageProgressRef.current = [];
      setStageProgress([]);
      controlsRef.current = null;
      rendererRef.current = null;
      threeSceneRef.current = null;
      cameraRef.current = null;
      meshesRef.current = [];
      renderer.setAnimationLoop(null);
      window.removeEventListener('keydown', onKeyDown);
      resizeObserver.disconnect();
      meshes.forEach(mesh => { scene.remove(mesh); mesh.dispose?.(); });
      if (xrayGroupRef.current) {
        scene.remove(xrayGroupRef.current);
        disposePipeGroup(xrayGroupRef.current);
        xrayGroupRef.current = null;
      }
      scene.remove(finalStageGroup);
      finalStageGroupRef.current = null;
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [stages, xrayPipeData]);

  if (stages.length < 1) {
    return <div className="fixed inset-0 grid place-items-center bg-black text-white">No .gaus scans have been added to this progression yet.</div>;
  }

  const lastStage = stages.length - 1;
  const hasComparison = stages.length > 1;
  const lower = hasComparison ? Math.min(Math.floor(progress), lastStage - 1) : 0;
  const fraction = hasComparison ? progress - lower : 0;
  const progressLabel = !hasComparison || fraction < 0.001
    ? stages[Math.round(progress)].label
    : `${stages[lower].label} → ${stages[lower + 1].label} · ${Math.round(fraction * 100)}%`;

  return (
    <div className="fixed inset-0 bg-black text-white">
      <div ref={mountRef} className="absolute inset-0" />
      <div className="absolute top-4 left-4 z-10 flex flex-col items-start gap-1 pointer-events-none select-none">
        <img src={cr3ateBird} alt="" className="h-8 w-auto opacity-90" />
        <img src={cr3ateLogo} alt="cr3ate" className="h-4 w-auto opacity-80" />
      </div>
      {loaded < stages.length && !error && (
        <div className="absolute inset-0 z-20 grid place-items-center bg-black">
          <div className="text-center">
            {stageProgress.length > 0 && stageProgress.some(p => p < 1) ? (
              <>
                <div className="text-sm">Downloading {stages.length} scan{stages.length !== 1 ? 's' : ''}…</div>
                <div className="flex gap-2 justify-center mt-3">
                  {stageProgress.map((p, i) => (
                    <div key={i} className="w-10 h-1.5 rounded-full bg-white/20 overflow-hidden">
                      <div
                        className="h-full bg-white/70 transition-[width] duration-300 ease-out"
                        style={{ width: `${Math.round(p * 100)}%` }}
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div>Loading captures… {loaded}/{stages.length}</div>
                <div className="mt-2 text-xs text-white/55">{loadingDetail}</div>
              </>
            )}
          </div>
        </div>
      )}
      {error && <div className="absolute inset-0 z-20 grid place-items-center bg-black text-red-300">{error}</div>}

      {playing && stageDisplayNames && stageDisplayNames.length > 0 && (() => {
        const idx = Math.round(Math.max(0, Math.min(stages.length - 1, progress)));
        const label = stageDisplayNames[idx] ?? stageDisplayNames[stageDisplayNames.length - 1];
        return (
          <div className="absolute left-1/2 -translate-x-1/2 z-10 pointer-events-none" style={{ top: '33%' }}>
            <div className="bg-black/50 backdrop-blur-sm text-white text-sm font-medium px-4 py-1.5 rounded-full">
              {label}
            </div>
          </div>
        );
      })()}

      {loaded >= stages.length && !error && (
        <ViewerOrchestrationAgent
          scene={agentScene}
          capabilities={PROGRESSION_CAPABILITIES}
          pois={[]}
          ready={true}
          onDecision={handleAgentDecision}
          onUserMessage={handleUserMessage}
          getViewerSpatialContext={getViewerSpatialContext}
          captureScreenshot={captureScreenshot}
          externalSubmitRef={externalSubmitRef}
          onGeneratedAssetReady={handleGeneratedAsset}
          onPillClick={handlePillClick}
        />
      )}

      <SelectionTool
        viewerRef={multiViewerRef}
        isActive={selectionActive}
        onClose={() => setSelectionActive(false)}
        onSelectionChange={(data) => { activeContextSelectionRef.current = data; }}
        initialMode={agentSelectionMode}
        onDone={(selection) => {
          activeContextSelectionRef.current = selection;
          setSelectionActive(false);
          const question = pendingSelectionQuestionRef.current;
          pendingSelectionQuestionRef.current = null;
          if (question) {
            externalSubmitRef.current?.(question);
          }
        }}
      />

      <EditSceneTool
        viewerRef={multiViewerRef as React.MutableRefObject<unknown>}
        isActive={editSceneActive}
        isAerial={false}
        onClose={() => { setEditSceneActive(false); setAgentEraseRegion(null); setAgentPlacement(null); }}
        assets={generatedAssets}
        initialAssetId={initialGeneratedAssetId}
        editAssetLabel={editAssetLabel}
        eraseModeToken={eraseModeToken}
        eraseBoxes={agentEraseBoxes}
        eraseRegion={agentEraseRegion ? { center: new THREE.Vector3(...agentEraseRegion.center), radius: agentEraseRegion.radius } : undefined}
        onEraseRegionDismissed={() => { setAgentEraseRegion(null); setEditSceneActive(false); }}
        agentPlacement={agentPlacement ?? undefined}
        onAgentPlacementDone={() => setAgentPlacement(null)}
      />

      {/* Yellow dashed border always visible when peek is active, panel open or not */}
      {peekBorderCorners && peekBorderCorners.length === 4 && (
        <PeekBorder viewerRef={multiViewerRef} corners={peekBorderCorners} />
      )}

      <PeekBehindWallTool
        viewerRef={multiViewerRef}
        stages={stages}
        isOpen={selectionPeekOpen}
        borderCorners={peekBorderCorners}
        onClose={() => setSelectionPeekOpen(false)}
        onApply={(selection, stageIndex) => {
          playingRef.current = false;
          setPlaying(false);
          const bgStage = Math.round(progress);
          controlsRef.current?.setSelectionPeek(selection, stageIndex, bgStage);
          setProgress(bgStage);
          setPeekPreConfigured(false);
          // Log bounds so they can be hardcoded for demo shortcuts.
          const b = selection.worldBounds;
          console.log(
            '[PeekBehindWall] selection bounds — paste into claudeAgent.ts shortcut:\n' +
            `  peekBounds: { min: [${b.min.x.toFixed(3)}, ${b.min.y.toFixed(3)}, ${b.min.z.toFixed(3)}], max: [${b.max.x.toFixed(3)}, ${b.max.y.toFixed(3)}, ${b.max.z.toFixed(3)}] },\n` +
            `  peekStageIndex: ${stageIndex},`
          );
        }}
        onClear={() => { controlsRef.current?.clearSelectionPeek(); setPeekPreConfigured(false); }}
        preConfiguredActive={peekPreConfigured}
        onPreConfiguredStageChange={(stageIndex) => {
          const bounds = peekPreConfiguredBoundsRef.current;
          if (bounds) controlsRef.current?.setPreConfiguredPeek(bounds, stageIndex, stages.length - 2);
        }}
      />

      {measureActive && (
        <MeasureTool
          key={measureKey}
          viewerRef={multiViewerRef}
          isActive={measureActive}
          onClose={() => { setMeasureActive(false); setMeasureInitialPoints(undefined); }}
          onPointsChange={() => {}}
          initialPoints={measureInitialPoints}
        />
      )}

      {sceneLabels.length > 0 && (
        <SceneLabelsLayer
          viewerRef={multiViewerRef}
          labels={sceneLabels}
          maxDistance={Infinity}
        />
      )}

      {activeTakeoffIndex !== null && takeoffItems?.[activeTakeoffIndex] && (
        <ObbTakeoffOverlay viewerRef={multiViewerRef} data={takeoffItems[activeTakeoffIndex]} />
      )}

      {wallTakeoffActive && wallTakeoffData && (
        <>
          <WallTakeoffOverlay
            viewerRef={multiViewerRef}
            data={wallTakeoffData}
            highlightedId={wallTakeoffHighlightedId}
          />
          <button
            type="button"
            onClick={() => setWallTakeoffActive(false)}
            style={{
              position: 'fixed',
              top: 12,
              right: 60,
              zIndex: 1025,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 10px',
              background: 'rgba(10,12,16,0.82)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 6,
              color: 'rgba(255,255,255,0.7)',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Close analysis
          </button>
        </>
      )}

      {progressComparisonReady && activeComparisonArea && (
        <ProgressComparisonOverlay viewerRef={multiViewerRef} area={activeComparisonArea} />
      )}

      {/* Hint when comparison is ready but no area selected yet */}
      {progressComparisonReady && !activeComparisonArea && (
        <div style={{
          position: 'fixed', bottom: 96, left: '50%', transform: 'translateX(-50%)',
          zIndex: 1024, pointerEvents: 'none',
          background: 'rgba(10,12,16,0.80)',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 8, padding: '8px 16px',
          fontSize: 12, color: 'rgba(255,255,255,0.55)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          whiteSpace: 'nowrap',
        }}>
          Click <span style={{ color: '#0CB4E4', fontWeight: 600 }}>Kitchen</span> or{' '}
          <span style={{ color: '#60a5fa', fontWeight: 600 }}>Staircase</span>{' '}
          in the chat to compare scans inside that area
        </div>
      )}

      {/* Stage picker panel — shown when an area is selected */}
      {progressComparisonReady && activeComparisonArea && (() => {
        const areaColor = COMPARISON_AREAS[activeComparisonArea].data.color;
        const areaName = COMPARISON_AREAS[activeComparisonArea].name;
        const btnStyle: React.CSSProperties = {
          background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 4, color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 700,
          width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', lineHeight: 1,
        };
        return (
          <div style={{
            position: 'fixed', bottom: 88, left: '50%', transform: 'translateX(-50%)',
            zIndex: 1024,
            background: 'rgba(10,12,16,0.88)',
            border: `1px solid ${areaColor}44`,
            borderRadius: 10, padding: '8px 14px',
            backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', gap: 10,
            userSelect: 'none', whiteSpace: 'nowrap',
          }}>
            <button style={btnStyle} onClick={() => handleComparisonLeft(-1)}>‹</button>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', minWidth: 52, textAlign: 'center' }}>
              {stages[comparisonLeftStage]?.label ?? `Scan ${comparisonLeftStage + 1}`}
            </span>
            <button style={btnStyle} onClick={() => handleComparisonLeft(1)}>›</button>
            <div style={{ width: 1, height: 20, background: `${areaColor}55`, margin: '0 2px' }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: areaColor }}>Stages</span>
            <div style={{ width: 1, height: 20, background: `${areaColor}55`, margin: '0 2px' }} />
            <button style={btnStyle} onClick={() => handleComparisonRight(-1)}>‹</button>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', minWidth: 52, textAlign: 'center' }}>
              {stages[comparisonRightStage]?.label ?? `Scan ${comparisonRightStage + 1}`}
            </span>
            <button style={btnStyle} onClick={() => handleComparisonRight(1)}>›</button>
          </div>
        );
      })()}

      {progressComparisonReady && (
        <button
          type="button"
          onClick={() => {
            setProgressComparisonReady(false);
            setActiveComparisonArea(null);
            controlsRef.current?.clearLocalCompare();
          }}
          style={{
            position: 'fixed',
            top: wallTakeoffActive ? 48 : 12,
            right: 60,
            zIndex: 1025,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 10px',
            background: 'rgba(10,12,16,0.82)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 6,
            color: 'rgba(255,255,255,0.7)',
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          Close comparison
        </button>
      )}

      {/* Takeoff side panel — sits left of the icon strip */}
      {activeTakeoffIndex !== null && takeoffItems?.[activeTakeoffIndex] && (
        <div className="absolute right-[64px] top-3 z-[1010]">
          <ObbTakeoffPanel
            data={takeoffItems[activeTakeoffIndex]}
            onClose={() => setActiveTakeoffIndex(null)}
          />
        </div>
      )}

      {/* Vertical icon toolbar + pop-out mini panels */}
      <div className={`absolute right-3 top-3 flex items-start gap-3 ${frontLayer === 'iconstrip' ? 'z-[1010]' : 'z-[1001]'}`}>

        {/* ── Time Lapse mini panel ── */}
        {openPanel === 'timelapse' && (
          <div className="w-[280px] rounded-xl bg-zinc-900/80 p-4 text-sm backdrop-blur-md">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-semibold text-white/60">{title}</span>
              <button onClick={() => setOpenPanel(null)} className="text-white/40 hover:text-white transition-colors">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            {/* Stage track */}
            <div className="mb-3 px-1">
              <div className="relative flex h-5 items-center">
                <div className="absolute inset-x-0 h-px bg-zinc-600" />
                <div
                  className="absolute left-0 h-px bg-cyan-400"
                  style={{ width: `${lastStage > 0 ? (progress / lastStage) * 100 : 0}%` }}
                />
                {stages.map((stage, index) => {
                  const pct = lastStage > 0 ? (index / lastStage) * 100 : 0;
                  const isPast = index <= Math.round(progress);
                  const isActive = Math.round(progress) === index;
                  return (
                    <button
                      key={`${stage.url}:${index}`}
                      onClick={() => {
                        playingRef.current = false;
                        setPlaying(false);
                        controlsRef.current?.setProgress(index);
                        setProgress(index);
                      }}
                      style={{ left: `${pct}%` }}
                      className={`absolute h-3 w-3 -translate-x-1/2 rounded-full border-2 transition-transform
                        ${isPast ? 'border-cyan-400 bg-cyan-400' : 'border-zinc-500 bg-zinc-900'}
                        ${isActive ? 'scale-[1.4]' : ''}`}
                    />
                  );
                })}
              </div>
              <div className="relative mt-1 h-4">
                {stages.map((stage, index) => {
                  const pct = lastStage > 0 ? (index / lastStage) * 100 : 0;
                  const isActive = Math.round(progress) === index;
                  return (
                    <span
                      key={index}
                      style={{ left: `${pct}%` }}
                      className={`absolute -translate-x-1/2 text-xs leading-none ${isActive ? 'font-semibold text-cyan-200' : 'text-white/45'}`}
                    >
                      {stage.label}
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="mb-3 flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-widest text-white/40">Time Lapse</span>
              <div className="flex-1 border-t border-white/10" />
            </div>

            <div className={`${hasComparison ? '' : 'pointer-events-none opacity-40'}`}>
              <div className="mb-1 text-xs text-white/50">Effect</div>
              <div className="mb-3 flex gap-1">
                {(['Fade', 'Wipe', 'Dissolve'] as const).map((label, value) => (
                  <button key={label} onClick={() => {
                    controlsRef.current?.setMode(value as BlendMode);
                    setMode(value as BlendMode);
                  }}
                    className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${mode === value ? 'bg-blue-500 text-white' : 'bg-zinc-800 text-white/60 hover:bg-zinc-700 hover:text-white'}`}>
                    {label}
                  </button>
                ))}
              </div>

              {mode === MODE.WIPE && (
                <div className="mb-3 flex gap-1">
                  {['X', 'Y', 'Z'].map((label, value) => (
                    <button key={label} onClick={() => { controlsRef.current?.setAxis(value); setAxis(value); }}
                      className={`flex-1 rounded-md px-2 py-1 text-xs ${axis === value ? 'bg-blue-500 text-white' : 'bg-zinc-800 text-white/60 hover:bg-zinc-700'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {mode !== MODE.FADE && (
                <label className="mb-3 block text-xs text-white/50">
                  Edge softness <span className="float-right text-cyan-200">{softness.toFixed(3)}</span>
                  <input className="mt-1 w-full" type="range" min="0.002" max="0.4" step="0.002" value={softness}
                    onChange={event => {
                      const value = Number(event.target.value);
                      controlsRef.current?.setSoftness(value);
                      setSoftness(value);
                    }} />
                </label>
              )}

              <div className="mb-1 flex justify-between text-xs">
                <span className="text-white/50">Blend</span>
                <span className="text-cyan-200">{progressLabel}</span>
              </div>
              <input className="mb-4 w-full" type="range" min={0} max={lastStage} step={hasComparison ? lastStage / 600 : 1} value={progress}
                onChange={event => {
                  const value = Number(event.target.value);
                  loopingRef.current = false;
                  playingRef.current = false;
                  setPlaying(false);
                  controlsRef.current?.setProgress(value);
                  setProgress(value);
                }} />

              <button onClick={() => {
                const nextPlaying = !playingRef.current;
                if (!nextPlaying) loopingRef.current = false;
                if (nextPlaying) {
                  const last = stages.length - 1;
                  if (progressRef.current >= last - 0.01) {
                    controlsRef.current?.setProgress(0);
                    setProgress(0);
                  }
                }
                playingRef.current = nextPlaying;
                setPlaying(nextPlaying);
              }}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-500 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-400 active:bg-blue-600">
                {playing ? (
                  <><svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>Pause</>
                ) : (
                  <><svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>Play Time Lapse</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ── Floor Map panel ── */}
        {openPanel === 'floormap' && (
          <div className="w-[440px]">
            <FloorMapPanel
              onTeleport={(pos, lookAt, floor) => {
                // Upper/basement: snap to second-to-last stage (that floor's latest scan).
                // Ground floor: snap to last stage (ground floor's latest scan is the final stage).
                if (floor === 'upper' || floor === 'basement') {
                  const target = Math.max(0, stages.length - 2);
                  controlsRef.current?.setProgress(target);
                  setProgress(target);
                } else {
                  const target = stages.length - 1;
                  controlsRef.current?.setProgress(target);
                  setProgress(target);
                }
                controlsRef.current?.teleportTo(pos, lookAt);
                setFloorMapHighlight(null);
              }}
              onClose={() => { setOpenPanel(null); setFloorMapHighlight(null); }}
              highlightedRoomName={floorMapHighlight?.roomName}
              initialFloor={floorMapHighlight?.floor}
            />
          </div>
        )}

        {/* ── Teleport + Reset mini panel ── */}
        {openPanel === 'teleport' && (
          <div className="w-[220px] rounded-xl bg-zinc-900/80 p-4 text-sm backdrop-blur-md">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-semibold text-white/60">Teleport</span>
              <button onClick={() => setOpenPanel(null)} className="text-white/40 hover:text-white transition-colors">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="mb-1 text-[10px] text-white/40">pos</div>
            <input
              ref={teleportInputRef}
              type="text"
              placeholder="x, y, z"
              onKeyDown={handleTeleportKeyDown}
              className="w-full rounded-md bg-zinc-800 px-2 py-1.5 text-xs text-white placeholder-white/25 outline-none focus:ring-1 focus:ring-cyan-500/60"
            />
            <div className="mb-1 mt-2 text-[10px] text-white/40">look</div>
            <input
              ref={teleportLookRef}
              type="text"
              placeholder="x, y, z"
              onKeyDown={handleTeleportKeyDown}
              className="w-full rounded-md bg-zinc-800 px-2 py-1.5 text-xs text-white placeholder-white/25 outline-none focus:ring-1 focus:ring-cyan-500/60"
            />
            <pre
              ref={cameraReadoutRef}
              className="mt-2 font-mono text-[10px] leading-relaxed text-white/35 select-all"
            >
              pos  —{'\n'}look —
            </pre>
          </div>
        )}

        {/* ── Icon strip ── */}
        <div className="flex flex-col gap-1 rounded-xl bg-zinc-900/80 p-1.5 backdrop-blur-md">
          {/* Time Lapse */}
          <button
            onClick={() => { setOpenPanel(p => p === 'timelapse' ? null : 'timelapse'); setFrontLayer('iconstrip'); }}
            title="Time Lapse"
            className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${openPanel === 'timelapse' ? 'bg-cyan-500/20 text-cyan-300' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="6" width="20" height="12" rx="2" />
              <line x1="2" y1="9" x2="22" y2="9" /><line x1="2" y1="15" x2="22" y2="15" />
              <line x1="6" y1="6" x2="6" y2="3" /><line x1="10" y1="6" x2="10" y2="3" />
              <line x1="14" y1="6" x2="14" y2="3" /><line x1="18" y1="6" x2="18" y2="3" />
              <line x1="6" y1="18" x2="6" y2="21" /><line x1="10" y1="18" x2="10" y2="21" />
              <line x1="14" y1="18" x2="14" y2="21" /><line x1="18" y1="18" x2="18" y2="21" />
            </svg>
          </button>

          {/* Peek Tool (only when xrayPipeData available) */}
          {xrayPipeData && (
            <button
              onClick={() => {
                setSelectionActive(false);
                setMeasureActive(false);
                setSelectionPeekOpen(v => !v);
                setPeekPreConfigured(false);
                setOpenPanel(null);
                setFrontLayer('overlay');
              }}
              title="Peek Tool"
              className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${selectionPeekOpen ? 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-400/40' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          )}

          {/* Floor Map */}
          <button
            onClick={() => { setOpenPanel(p => p === 'floormap' ? null : 'floormap'); setFrontLayer('iconstrip'); }}
            title="Floor Map"
            className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${openPanel === 'floormap' ? 'bg-cyan-500/20 text-cyan-300' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="8" height="8" rx="1" />
              <rect x="13" y="3" width="8" height="8" rx="1" />
              <rect x="3" y="13" width="8" height="8" rx="1" />
              <rect x="13" y="13" width="8" height="8" rx="1" />
            </svg>
          </button>

          {/* Measure */}
          <button
            onClick={() => {
              setSelectionActive(false);
              setMeasureKey(k => k + 1);
              setMeasureInitialPoints(undefined);
              setMeasureActive(v => !v);
              setOpenPanel(null);
              setFrontLayer('overlay');
            }}
            title="Measure"
            className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${measureActive ? 'bg-cyan-500/20 text-cyan-300 ring-1 ring-cyan-400/40' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12h20" />
              <path d="M2 12l3-3M2 12l3 3" />
              <path d="M22 12l-3-3M22 12l-3 3" />
              <line x1="8" y1="9" x2="8" y2="15" />
              <line x1="12" y1="7" x2="12" y2="17" />
              <line x1="16" y1="9" x2="16" y2="15" />
            </svg>
          </button>

          {/* Teleport / Reset */}
          <button
            onClick={() => { setOpenPanel(p => p === 'teleport' ? null : 'teleport'); setFrontLayer('iconstrip'); }}
            title="Teleport / Reset"
            className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${openPanel === 'teleport' ? 'bg-cyan-500/20 text-cyan-300' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <line x1="12" y1="3" x2="12" y2="6" />
              <line x1="12" y1="18" x2="12" y2="21" />
              <line x1="3" y1="12" x2="6" y2="12" />
              <line x1="18" y1="12" x2="21" y2="12" />
              <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
            </svg>
          </button>

          {/* Edit Scene (3D object placement) */}
          <button
            onClick={() => {
              setEditSceneActive(v => !v);
              setOpenPanel(null);
              setFrontLayer('overlay');
            }}
            title="Edit Scene"
            className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${editSceneActive ? 'bg-cyan-500/20 text-cyan-300 ring-1 ring-cyan-400/40' : 'text-white/50 hover:bg-white/10 hover:text-white'}`}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </button>

          {/* Reset camera */}
          <button
            onClick={() => controlsRef.current?.resetView()}
            title="Reset view (H)"
            className="flex h-10 w-10 items-center justify-center rounded-lg text-white/50 hover:bg-white/10 hover:text-white transition-colors"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </button>

        </div>
      </div>
    </div>
  );
}
